import React, { useEffect, useMemo, useState } from 'react'
import { useAppSelector, RootState, useAppDispatch } from '../utils/agent.store'
import {
  SvgReadOnly,
  SvgSearchIcon,
  SelectDropDown,
  CustomButton,
  Tabs,
  TabInterface,
} from '@ui-kit'
import {
  DropdownType,
  DataTypeStore,
  getProgramsList,
  HCPProgramType,
  getPermissionsByProgramId,
  showLoader,
  hideLoader,
  DropDownOptionType,
  getClientPermissionsByPortalIdApi,
  AuthUserClient,
} from '@app-store'
import { MY_PROFILE_STRINGS } from '../utils/strings'
import { NAMESPACES, USER_PROFILES } from '../utils/constants'
import { ProgramPermissionsResponseDataType } from '../types/permission.types'
import { DropDownOption } from '../types/types'

interface PermissionsType {
  id: number
  name: string
  permission_type: 'NONE' | 'ADMIN'
  services: {
    service_id: number
    service_name: string
  }[]
}

interface PermissionSetType {
  id: number
  permission_set_name: string
  permissions: PermissionsType[]
  set_type: string
  status: string
}

interface MyProfileProps {
  setOpenProfilePanel: React.Dispatch<React.SetStateAction<boolean>>
}

const MyProfile = ({ setOpenProfilePanel }: MyProfileProps) => {
  const user = useAppSelector((state: RootState) => state.authUser.user)
  const agentStatus = useAppSelector((state) => state.awsCall.agentStatus)
  const selectedClientTenant = useAppSelector(
    (state: RootState) => state.authUser.selectedClientTenant
  )
  const [selectedTenantId, setSelectedTenantId] = useState<number | undefined>(
    selectedClientTenant?.id
  )
  const allTenants = useAppSelector(
    (state: RootState) => state?.authUser?.user?.clients
  )
  const [allTenantsOptions, setAllTenantsOptions] = useState<
    DropdownType<DropDownOption>[]
  >([])
  const dispatch = useAppDispatch()
  const [selectedSet, setSelectedSet] = useState<string>('')
  const [allTabs, setAllTabs] = useState<TabInterface[]>([])
  const [selectedGlobalSet, setSelectedGlobalSet] = useState<string>('')
  const [search, setSearch] = useState<string>('')
  const [searchGlobal, setSearchGlobal] = useState<string>('')
  const [permissions, setPermissions] = useState<PermissionsType[]>([])
  const [globalPermissions, setGlobalPermissions] = useState<PermissionsType[]>(
    []
  )
  const [programList, setProgramList] = useState<DropdownType<DataTypeStore>[]>(
    []
  )
  const [permissionSetData, setPermissionSetData] = useState<
    PermissionSetType[]
  >([])
  const [globalPermissionSetData, setGlobalPermissionSetData] = useState<
    PermissionSetType[]
  >([])
  const [permissionSetNames, setPermissionSetNames] = useState<string[]>([])
  const [globalPermissionSetNames, setGlobalPermissionSetNames] = useState<
    string[]
  >([])
  const [selectedProgramId, setSelectedProgramId] = useState<string>('')
  const [activeTab, setActiveTab] = useState<TabInterface>()

  const agentName = `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim()
  const agentEmail = user?.user_id ?? ''
  const userProfileType = user?.profile ?? ''

  const getAllTenantOptions = () => {
    const options: { label: string; value: string }[] = []

    const enabledClients = allTenants?.filter(
      (item: AuthUserClient) => item.enabled
    )
    const globalEntry = enabledClients?.find(
      (item) => item.name_of_organization === 'Global'
    )
    const sortedClients = enabledClients
      ?.filter((item) => item.name_of_organization !== 'Global')
      .sort((a, b) =>
        a.name_of_organization.localeCompare(b.name_of_organization)
      )
    if (globalEntry)
      options.push({
        label: globalEntry.name_of_organization,
        value: globalEntry.id?.toString() || '',
      })
    sortedClients?.forEach((item: AuthUserClient) => {
      options.push({
        label: item.name_of_organization,
        value: item.id?.toString() || '',
      })
    })
    setAllTenantsOptions(options)
  }

  const fetchPrograms = async (value?: number) => {
    try {
      dispatch(showLoader())
      if (
        user?.profile === USER_PROFILES.PROGRAM_ADMIN ||
        user?.profile === USER_PROFILES.SUPERADMIN
      ) {
        const programs = await getProgramsList(value ?? selectedTenantId)
        if (programs?.data?.content) {
          const programOptionsList: DropdownType<DataTypeStore>[] =
            programs?.data?.content
              ?.filter((item: HCPProgramType) => item.enabled)
              ?.map((item: HCPProgramType) => {
                return {
                  label: item?.operational_program_name ?? '',
                  value: item?.id?.toString() ?? '',
                }
              })
          setProgramList([...programOptionsList])
          setSelectedProgramId(programOptionsList[0]?.value)
        }
      } else if (
        user?.profile === USER_PROFILES.STANDARD_USER ||
        user?.profile === USER_PROFILES.SUPPORT
      ) {
        let programOptionsList: DropdownType<DataTypeStore>[] = []
        const response = await getClientPermissionsByPortalIdApi(
          value ?? selectedTenantId
        )
        programOptionsList = response.data.program_permissions.map(
          (programPermission: ProgramPermissionsResponseDataType) => ({
            label: programPermission.operational_program_name,
            value: programPermission.program_id.toString(),
          })
        )
        setProgramList([...programOptionsList])
        setSelectedProgramId(programOptionsList[0]?.value)
      }
    } catch (err) {
      console.error(err)
      setProgramList([])
    } finally {
      dispatch(hideLoader())
    }
  }

  const filteredSets = [...(permissionSetNames || [])]
    .filter((name) => name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => a.localeCompare(b))

  const globalFilteredSets = [...(globalPermissionSetNames || [])]
    .filter((name) => name.toLowerCase().includes(searchGlobal.toLowerCase()))
    .sort((a, b) => a.localeCompare(b))

  const sortedPermissions = [...permissions].sort((a, b) =>
    a.name.localeCompare(b.name)
  )

  const sortedGlobalPermissions = [...globalPermissions].sort((a, b) =>
    a.name.localeCompare(b.name)
  )

  const handleProgramChange = (e: DropdownType<unknown, string>): void => {
    if (e?.value) {
      setSelectedProgramId(e.value)
    }
  }

  const onClientChange = (value: DropdownType<DropDownOptionType>) => {
    setSelectedTenantId(Number(value.value))
    if (Number(value.value) !== -1) {
      fetchPrograms(Number(value.value))
    }
    setSelectedProgramId('')
    setSearch('')
  }

  const fetchPermissionsByProgram = async () => {
    try {
      dispatch(showLoader())
      const response = await getPermissionsByProgramId(
        selectedTenantId === -1 ? 0 : selectedProgramId,
        selectedTenantId === -1 ? '' : selectedTenantId ?? -1
      )
      const permissionSets =
        response?.data?.programs?.[0]?.permission_sets || []
      const currPermissionSetNames = permissionSets?.map(
        (set: (typeof response.data.permission_sets)[0]) => {
          return set.permission_set_name
        }
      )
      setPermissionSetNames(currPermissionSetNames)
      setPermissionSetData(response?.data?.programs?.[0]?.permission_sets)
      const sortedPermissionSetNames = [...currPermissionSetNames].sort(
        (a, b) => a.localeCompare(b)
      )
      if (sortedPermissionSetNames.length > 0) {
        setSelectedSet(sortedPermissionSetNames[0])
      } else {
        setSelectedSet('')
      }
      const globalSets = response?.data?.global || []
      const globalPermissionSets = globalSets?.map(
        (set: (typeof response.data.global)[0]) => {
          return set.permission_set_name
        }
      )
      setGlobalPermissionSetNames(globalPermissionSets)
      setGlobalPermissionSetData(response?.data?.global)
      const sortedGlobalPermissionSets = [...globalPermissionSets].sort(
        (a, b) => a.localeCompare(b)
      )
      if (sortedGlobalPermissionSets.length > 0) {
        setSelectedGlobalSet(sortedGlobalPermissionSets[0])
      } else {
        setSelectedGlobalSet('')
      }
    } catch (err) {
      console.error(err)
      setPermissionSetNames([])
      setPermissionSetData([])
      setGlobalPermissionSetNames([])
      setGlobalPermissionSetData([])
    } finally {
      dispatch(hideLoader())
    }
  }

  const ProgramPermissions = useMemo(
    () => (
      <div className="flex border rounded-md flex-1 min-h-0">
        <div className="w-[30%] border-r p-3 flex flex-col">
          <div className="text-sm font-medium mb-2.5">
            {MY_PROFILE_STRINGS.ASSIGNED_PERMISSIONS_SET}
          </div>
          <div className="relative w-full mb-1">
            <SvgSearchIcon
              className="absolute left-2 top-1/2 -translate-y-1/2 text-accent-600"
              height="0.75rem"
              width="0.75rem"
            />
            <input
              type="text"
              placeholder={MY_PROFILE_STRINGS.SEARCH_PERMISSIONS_SET}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full py-2.5 pl-7 pr-3 text-xs border border-accent-300 focus:outline-none font-light"
            />
          </div>
          <div className="flex flex-col gap-1 pb-1 max-h-[calc(100vh-335px)] overflow-y-auto pr-1 flex-1 min-h-0">
            {filteredSets.map((set) => (
              <button
                key={set}
                onClick={() => setSelectedSet(set)}
                className={`cursor-pointer items-center rounded border-l-2 border-secondary-500 flex p-2 text-xs text-accent-800 font-medium shadow-md
                      ${
                        selectedSet === set
                          ? 'bg-blue-50 hover:bg-blue-100'
                          : 'hover:bg-gray-100'
                      }`}
              >
                {set}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 min-h-0 flex flex-col">
          <div className="text-sm font-medium mb-3 border-b p-3">
            {MY_PROFILE_STRINGS.ASSIGNED_PERMISSIONS_WITHIN_PERMISSION_SET}
          </div>
          <div className="flex flex-wrap gap-2.5 border rounded border-gray-100 px-2.5 py-3 my-3 mx-2.5 flex-1 min-h-0 overflow-y-auto max-h-max">
            {sortedPermissions.length > 0 ? (
              sortedPermissions.map((perm) => (
                <span
                  key={perm.id}
                  className="px-3 py-1.5 text-xs font-medium font-[Quicksand] text-accent-800 bg-blue-50 rounded-[100px] border-1 border-primary-25"
                >
                  {perm.name}
                </span>
              ))
            ) : (
              <p className="text-accent-500 text-sm">
                {MY_PROFILE_STRINGS.NO_PERMISSIONS_MESSAGE}
              </p>
            )}
          </div>
        </div>
      </div>
    ),
    [search, filteredSets, sortedPermissions, selectedSet]
  )

  useEffect(() => {
    const calculatedTabs = [
      {
        label: 'Program',
        component: null,
      },
      {
        label: 'Global',
        component: null,
      },
    ]
    setAllTabs(calculatedTabs)
    if (calculatedTabs.length > 0) {
      const isCurrentTabValid = calculatedTabs.some(
        (tab) => tab.label === activeTab?.label
      )
      if (!activeTab || !isCurrentTabValid) {
        setActiveTab(calculatedTabs[0])
      }
    } else {
      setActiveTab(undefined)
    }
  }, [permissionSetNames?.length, globalPermissionSetNames?.length])

  useEffect(() => {
    getAllTenantOptions()
  }, [allTenants])

  useEffect(() => {
    if (selectedTenantId !== -1) {
      fetchPrograms()
    } else {
      fetchPermissionsByProgram()
    }
  }, [selectedTenantId])

  useEffect(() => {
    if (selectedProgramId && programList.length > 0) {
      fetchPermissionsByProgram()
    }
  }, [selectedProgramId])

  useEffect(() => {
    if (selectedSet) {
      const selectedPermissionSet = permissionSetData.find(
        (set: PermissionSetType) => set.permission_set_name === selectedSet
      )
      if (selectedPermissionSet?.permissions) {
        setPermissions(selectedPermissionSet?.permissions)
      }
    }
  }, [selectedSet, permissionSetData])

  useEffect(() => {
    if (selectedGlobalSet) {
      const selectedSet = globalPermissionSetData.find(
        (set) => set.permission_set_name === selectedGlobalSet
      )
      if (selectedSet?.permissions) {
        setGlobalPermissions(selectedSet.permissions)
      }
    }
  }, [selectedGlobalSet, globalPermissionSetData])

  return (
    <div className="flex flex-col h-full font-primary">
      <div className="leading-4 px-2 py-3 border-b border-accent-200">
        <div className="flex items-center justify-between flex-wrap gap-y-2">
          <div>{MY_PROFILE_STRINGS.MY_PROFILE}</div>
          <CustomButton
            isFilled={true}
            onClick={() => setOpenProfilePanel(false)}
          >
            {NAMESPACES.CLOSE}
          </CustomButton>
        </div>
      </div>

      <div className="flex justify-between m-2 mb-1 py-5 px-4 bg-blue-50 border-[0.5px] border-accent-300 rounded text-sm font-medium font-[Quicksand]">
        <div>
          <span>
            {MY_PROFILE_STRINGS.AGENT_NAME}
            {': '}
            <span className="text-accent-500">{agentName}</span>
          </span>
          <span>{agentStatus} </span>
\        </div>
        <span>
          {MY_PROFILE_STRINGS.AGENT_EMAIL}
          {': '}
          <span className="text-accent-500">{agentEmail}</span>
        </span>
        <span>
          {MY_PROFILE_STRINGS.PROFILE}
          {': '}
          <span className="text-accent-500">{userProfileType}</span>
        </span>
      </div>
      <div className="p-2 flex-1 min-h-0 flex flex-col">
        {user?.profile !== MY_PROFILE_STRINGS.READ_ONLY_USER ? (
          user?.profile === USER_PROFILES.STANDARD_USER ||
          user?.profile === USER_PROFILES.SUPPORT ? (
            <>
              <Tabs
                onChange={(_label: string, index: number) =>
                  setActiveTab(allTabs[index])
                }
                tabs={allTabs}
                defaultActive={activeTab}
              />
              {activeTab?.label === 'Global' ? (
                (globalPermissionSetNames?.length || 0) > 0 ? (
                  <div className="flex border rounded-md mt-3 flex-1 min-h-0">
                    <div className="w-[30%] border-r p-3 flex flex-col">
                      <div className="text-sm font-medium mb-2.5">
                        {MY_PROFILE_STRINGS.ASSIGNED_PERMISSIONS_SET}
                      </div>
                      <div className="relative w-full mb-1">
                        <SvgSearchIcon
                          className="absolute left-2 top-1/2 -translate-y-1/2 text-accent-600"
                          height="0.75rem"
                          width="0.75rem"
                        />
                        <input
                          type="text"
                          placeholder={
                            MY_PROFILE_STRINGS.SEARCH_PERMISSIONS_SET
                          }
                          value={searchGlobal}
                          onChange={(e) => setSearchGlobal(e.target.value)}
                          className="w-full py-2.5 pl-7 pr-3 text-xs border border-accent-300 focus:outline-none font-light"
                        />
                      </div>
                      <div className="flex flex-col gap-1 pb-1 max-h-[calc(100vh-335px)] overflow-y-auto pr-1 flex-1 min-h-0">
                        {globalFilteredSets.map((set) => (
                          <button
                            key={set}
                            onClick={() => setSelectedGlobalSet(set)}
                            className={`cursor-pointer items-center rounded border-l-2 border-secondary-500 flex p-2 text-xs text-accent-800 font-medium shadow-md
                      ${
                        selectedGlobalSet === set
                          ? 'bg-blue-50 hover:bg-blue-100'
                          : 'hover:bg-gray-100'
                      }`}
                          >
                            {set}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="flex-1 min-h-0 flex flex-col">
                      <div className="text-sm font-medium mb-3 border-b p-3">
                        {
                          MY_PROFILE_STRINGS.ASSIGNED_PERMISSIONS_WITHIN_PERMISSION_SET
                        }
                      </div>
                      <div className="flex flex-wrap gap-2.5 border rounded border-gray-100 px-2.5 py-3 my-3 mx-2.5 flex-1 min-h-0 overflow-y-auto max-h-max">
                        {sortedGlobalPermissions.length > 0 ? (
                          sortedGlobalPermissions.map((perm) => (
                            <span
                              key={perm.id}
                              className="px-3 py-1.5 text-xs font-medium font-[Quicksand] text-accent-800 bg-blue-50 rounded-[100px] border-1 border-primary-25"
                            >
                              {perm.name}
                            </span>
                          ))
                        ) : (
                          <p className="text-accent-500 text-sm">
                            {MY_PROFILE_STRINGS.NO_PERMISSIONS_MESSAGE}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-center items-center border rounded-md flex-1 min-h-0">
                    {MY_PROFILE_STRINGS.NEGATIVE_SCREEN_GLOBAL}
                  </div>
                )
              ) : (permissionSetNames?.length || 0) > 0 ? (
                <>
                  <div className="flex items-center gap-4 text-xs text-accent-500 pr-3 my-3">
                    <div className="flex items-center gap-1">
                      <span>Client{':'}</span>
                      <SelectDropDown
                        options={allTenantsOptions}
                        label=""
                        onChange={onClientChange}
                        name=""
                        defaultValue={selectedTenantId?.toString()}
                        isCrossable={false}
                        className="bg-secondary-50 h-10 w-[8rem] text-xs"
                      />
                    </div>
                    {selectedTenantId !== -1 ? (
                      <div className="flex items-center gap-1">
                        <span>Program{':'}</span>
                        <SelectDropDown
                          placeholder="Select Program"
                          defaultValue={selectedProgramId}
                          onChange={handleProgramChange}
                          options={programList}
                          label={''}
                          name={''}
                          isCrossable={false}
                          className="bg-secondary-50 h-10 text-xs"
                        />
                      </div>
                    ) : null}
                  </div>
                  {ProgramPermissions}
                </>
              ) : (
                <>
                  <div className="flex items-center gap-4 text-xs text-accent-500 pr-3 my-3">
                    <div className="flex items-center gap-1">
                      <span>Client{':'}</span>
                      <SelectDropDown
                        options={allTenantsOptions}
                        label=""
                        onChange={onClientChange}
                        name=""
                        isCrossable={false}
                        className="bg-secondary-50 h-10 w-[8rem]text-xs"
                      />
                    </div>
                    {selectedTenantId !== -1 ? (
                      <div className="flex items-center gap-1">
                        <span>Program{':'}</span>
                        <SelectDropDown
                          placeholder="Select Program"
                          defaultValue={selectedProgramId}
                          onChange={handleProgramChange}
                          options={programList}
                          label={''}
                          name={''}
                          isCrossable={false}
                          className="bg-secondary-50 h-10 text-xs"
                        />
                      </div>
                    ) : null}
                  </div>
                  <div className="flex justify-center items-center border rounded-md flex-1 min-h-0">
                    {MY_PROFILE_STRINGS.NEGATIVE_SCREEN_PROGRAM}
                  </div>
                </>
              )}
            </>
          ) : (
            ProgramPermissions
          )
        ) : (
          <div className="bg-[#F2F8FF80] border-none rounded-md p-2 text-center flex flex-col items-center justify-center min-h-[14.5rem]">
            <div>
              <SvgReadOnly className="text-blue-200 mx-auto mb-3" />
            </div>
            <div>
              <p className="text-sm font-primary font-light text-accent-900">
                {MY_PROFILE_STRINGS.READ_ONLY_USER_ACCESS_MESSAGE}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default MyProfile
